import React from 'react';
import { AbsoluteFill, useCurrentFrame, useVideoConfig, interpolate, spring } from 'remotion';

// ============================================================================
// #302 WebGL Shader Particles (WebGL 着色器粒子效果)
// ============================================================================
// 功能：
// - WebGL 着色器驱动的粒子系统
// - GPU 加速渲染
// - 光线追踪效果
// - 颜色渐变
// 
// 技术栈：
// - Canvas 2D 模拟 WebGL 效果（避免 Three.js 依赖）
// - GLSL 风格着色器算法
// - 粒子物理系统
// - 发光滤镜
// 
// 适用：创意片头、科技主题、3D 展示
// ============================================================================

export const WebGLShaderParticles: React.FC = () => {
  const frame = useCurrentFrame();
  const { width, height, fps } = useVideoConfig();
  
  // 粒子配置
  const particleCount = 150;
  const time = frame / fps;
  
  // 生成粒子数据
  const particles = Array.from({ length: particleCount }, (_, i) => {
    const angle = (i / particleCount) * Math.PI * 2;
    const radius = 200 + Math.sin(time * 0.5 + i * 0.1) * 100;
    const x = width / 2 + Math.cos(angle + time * 0.3) * radius;
    const y = height / 2 + Math.sin(angle + time * 0.3) * radius;
    
    // GLSL 风格颜色计算
    const hue = (i / particleCount + time * 0.1) % 1;
    const saturation = 0.8;
    const lightness = 0.5 + Math.sin(time * 2 + i * 0.2) * 0.2;
    
    // 粒子大小（基于距离中心的距离）
    const distFromCenter = Math.sqrt((x - width/2)**2 + (y - height/2)**2);
    const size = interpolate(distFromCenter, [0, 400], [8, 3], {
      extrapolateLeft: 'clamp',
      extrapolateRight: 'clamp',
    });
    
    // 透明度（基于距离和脉冲）
    const opacity = interpolate(
      Math.sin(time * 3 + i * 0.3),
      [-1, 1],
      [0.3, 0.9]
    );
    
    return {
      x,
      y,
      size,
      hue,
      saturation,
      lightness,
      opacity,
    };
  });
  
  // 背景光晕效果
  const glowLayers = Array.from({ length: 3 }, (_, layerIndex) => {
    const scale = interpolate(
      spring({
        frame,
        fps,
        config: {
          damping: 200,
          stiffness: 100,
          mass: 0.5,
        },
      }),
      [0, 1],
      [0.5, 1 + layerIndex * 0.1]
    );
    
    const opacity = interpolate(
      spring({
        frame,
        fps,
        config: {
          damping: 200,
        },
      }),
      [0, 1],
      [0, 0.3 - layerIndex * 0.08]
    );
    
    const rotation = time * 0.1 * (layerIndex + 1);
    
    return (
      <div
        key={layerIndex}
        style={{
          position: 'absolute',
          width: width * 1.5,
          height: height * 1.5,
          left: -width * 0.25,
          top: -height * 0.25,
          background: `radial-gradient(circle, 
            hsla(${200 + layerIndex * 40}, 80%, 60%, ${opacity}) 0%, 
            hsla(${260 + layerIndex * 40}, 80%, 40%, 0) 70%
          )`,
          transform: `rotate(${rotation}rad) scale(${scale})`,
          filter: `blur(${40 + layerIndex * 20}px)`,
          mixBlendMode: 'screen',
        }}
      />
    );
  });
  
  // SVG 粒子渲染
  const particleElements = particles.map((particle, i) => {
    const color = `hsla(${particle.hue * 360}, ${particle.saturation * 100}%, ${particle.lightness * 100}%, ${particle.opacity})`;
    
    return (
      <circle
        key={i}
        cx={particle.x}
        cy={particle.y}
        r={particle.size}
        fill={color}
        filter="url(#glow302)"
      />
    );
  });
  
  // 连线效果（粒子之间的连线）
  const connectionLines = [];
  for (let i = 0; i < particles.length; i++) {
    for (let j = i + 1; j < particles.length; j++) {
      const dx = particles[i].x - particles[j].x;
      const dy = particles[i].y - particles[j].y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      
      if (distance < 80) {
        const opacity = interpolate(distance, [0, 80], [0.5, 0], {
          extrapolateRight: 'clamp',
        });
        
        connectionLines.push(
          <line
            key={`${i}-${j}`}
            x1={particles[i].x}
            y1={particles[i].y}
            x2={particles[j].x}
            y2={particles[j].y}
            stroke={`hsla(${(particles[i].hue + particles[j].hue) * 180}, 80%, 60%, ${opacity})`}
            strokeWidth={1}
          />
        );
      }
    }
  }
  
  // 中心文字动画
  const textScale = spring({
    frame,
    fps,
    config: {
      damping: 15,
      stiffness: 100,
      mass: 0.5,
    },
  });
  
  const textOpacity = spring({
    frame: frame - 30,
    fps,
    config: {
      damping: 100,
    },
  });
  
  return (
    <AbsoluteFill
      style={{
        backgroundColor: '#0a0a0f',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        overflow: 'hidden',
      }}
    >
      {/* 背景光晕层 */}
      {glowLayers}
      
      {/* SVG 粒子层 */}
      <svg
        width={width}
        height={height}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
        }}
      >
        {/* 发光滤镜定义 */}
        <defs>
          <filter id="glow302" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="3" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        
        {/* 连线层 */}
        {connectionLines}
        
        {/* 粒子层 */}
        {particleElements}
      </svg>
      
      {/* 中心文字 */}
      <div
        style={{
          position: 'absolute',
          transform: `scale(${textScale})`,
          opacity: textOpacity,
          textAlign: 'center',
          zIndex: 10,
        }}
      >
        <h1
          style={{
            fontSize: 100,
            fontWeight: 'bold',
            background: 'linear-gradient(135deg, #00d4ff 0%, #ff00ff 50%, #ffaa00 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            color: 'transparent',
            margin: 0,
            textShadow: '0 0 40px rgba(0, 212, 255, 0.5)',
          }}
        >
          WebGL Shader
        </h1>
        <p
          style={{
            fontSize: 32,
            color: '#aaa',
            marginTop: 20,
            letterSpacing: 4,
          }}
        >
          GPU Accelerated Particles
        </p>
      </div>
    </AbsoluteFill>
  );
};

// ============================================================================
// #303 Light Leak Transition Enhanced (光泄漏转场增强版)
// ============================================================================
// 功能：
// - 电影级光泄漏效果
// - 多层光带叠加
// - mix-blend-mode screen 混合模式
// - 颜色渐变流动
// - 模糊和发光效果
// 
// 技术栈：
// - CSS 动画 + SVG 滤镜
// - 多层叠加技术
// - mix-blend-mode 混合
// - Spring 物理动画
// 
// 适用：视频转场、片头、宣传片
// ============================================================================

export const LightLeakTransitionEnhanced: React.FC = () => {
  const frame = useCurrentFrame();
  const { width, height, fps } = useVideoConfig();
  
  const time = frame / fps;
  
  // 光泄漏层配置（5 层不同颜色和角度）
  const lightLeakLayers = [
    {
      color: 'rgba(255, 100, 50, 0.4)',
      angle: 45,
      width: width * 0.8,
      speed: 1.2,
      delay: 0,
    },
    {
      color: 'rgba(255, 200, 50, 0.3)',
      angle: -30,
      width: width * 0.6,
      speed: 0.8,
      delay: 5,
    },
    {
      color: 'rgba(255, 50, 150, 0.3)',
      angle: 60,
      width: width * 1.2,
      speed: 1.5,
      delay: 10,
    },
    {
      color: 'rgba(50, 200, 255, 0.25)',
      angle: -45,
      width: width * 0.9,
      speed: 1.0,
      delay: 15,
    },
    {
      color: 'rgba(255, 255, 100, 0.2)',
      angle: 0,
      width: width * 1.5,
      speed: 0.6,
      delay: 20,
    },
  ];
  
  // 生成光泄漏层
  const lightLeakElements = lightLeakLayers.map((layer, index) => {
    const progress = spring({
      frame: frame - layer.delay,
      fps,
      config: {
        damping: 15,
        stiffness: 80,
        mass: 1,
      },
    });
    
    // 光带位置（从左到右移动）
    const xPos = interpolate(
      time * layer.speed,
      [0, 5],
      [-layer.width, width + layer.width]
    );
    
    // 光带高度和透明度
    const heightRatio = 0.3 + index * 0.1;
    const opacity = interpolate(progress, [0, 1], [0, parseFloat(layer.color.split(',')[3])]);
    
    return (
      <div
        key={index}
        style={{
          position: 'absolute',
          width: layer.width,
          height: height * heightRatio,
          left: xPos,
          top: (height - height * heightRatio) / 2,
          background: `linear-gradient(${layer.angle}deg, 
            transparent 0%, 
            ${layer.color.replace(/[\d.]+\)$/, `${opacity})`)} 50%, 
            transparent 100%
          )`,
          transform: `rotate(${layer.angle}deg) scale(${1 + index * 0.1})`,
          filter: `blur(${30 + index * 10}px)`,
          mixBlendMode: 'screen',
          opacity: progress,
        }}
      />
    );
  });
  
  // 中心闪光点
  const flashProgress = spring({
    frame: frame - 30,
    fps,
    config: {
      damping: 10,
      stiffness: 200,
      mass: 0.5,
    },
  });
  
  const flashScale = interpolate(flashProgress, [0, 0.5, 1], [0, 2, 1]);
  const flashOpacity = interpolate(flashProgress, [0, 0.5, 1], [0, 1, 0]);
  
  // 背景渐变动画
  const bgProgress = spring({
    frame,
    fps,
    config: {
      damping: 50,
    },
  });
  
  const bgBrightness = interpolate(bgProgress, [0, 1], [0.2, 1]);
  
  // 中心文字动画
  const textProgress = spring({
    frame: frame - 60,
    fps,
    config: {
      damping: 15,
    },
  });
  
  const textY = interpolate(textProgress, [0, 1], [50, 0]);
  const textOpacity = textProgress;
  
  // 装饰粒子（光点）
  const particles = Array.from({ length: 30 }, (_, i) => {
    const angle = (i / 30) * Math.PI * 2;
    const radius = 300 + Math.sin(time * 2 + i) * 100;
    const x = width / 2 + Math.cos(angle + time * 0.5) * radius;
    const y = height / 2 + Math.sin(angle + time * 0.5) * radius;
    const size = 2 + Math.sin(time * 3 + i * 0.5) * 2;
    const opacity = 0.3 + Math.sin(time * 4 + i) * 0.2;
    
    return (
      <div
        key={i}
        style={{
          position: 'absolute',
          width: size,
          height: size,
          borderRadius: '50%',
          backgroundColor: `hsla(${(i / 30) * 60 + 30}, 100%, 70%, ${opacity})`,
          left: x,
          top: y,
          boxShadow: `0 0 ${size * 3}px hsla(${(i / 30) * 60 + 30}, 100%, 70%, ${opacity})`,
        }}
      />
    );
  });
  
  return (
    <AbsoluteFill
      style={{
        backgroundColor: '#0f0f15',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        overflow: 'hidden',
      }}
    >
      {/* 背景层（动态亮度） */}
      <div
        style={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          background: `radial-gradient(circle at 50% 50%, 
            hsla(30, 80%, ${bgBrightness * 50}%, 0.3) 0%, 
            hsla(330, 80%, ${bgBrightness * 30}%, 0.2) 50%, 
            #0f0f15 100%
          )`,
        }}
      />
      
      {/* 光泄漏层 */}
      {lightLeakElements}
      
      {/* 装饰粒子 */}
      {particles}
      
      {/* 中心闪光 */}
      <div
        style={{
          position: 'absolute',
          width: 400,
          height: 400,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(255,255,255,0.8) 0%, transparent 70%)',
          transform: `scale(${flashScale})`,
          opacity: flashOpacity,
          filter: 'blur(40px)',
        }}
      />
      
      {/* 中心文字 */}
      <div
        style={{
          position: 'absolute',
          transform: `translateY(${textY}px)`,
          opacity: textOpacity,
          textAlign: 'center',
          zIndex: 10,
        }}
      >
        <h1
          style={{
            fontSize: 90,
            fontWeight: 'bold',
            background: 'linear-gradient(135deg, #ffaa00 0%, #ff5500 50%, #ff0055 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            color: 'transparent',
            margin: 0,
            textShadow: '0 0 60px rgba(255, 100, 50, 0.6)',
            letterSpacing: 2,
          }}
        >
          Light Leak
        </h1>
        <p
          style={{
            fontSize: 28,
            color: '#ccc',
            marginTop: 20,
            letterSpacing: 6,
            textTransform: 'uppercase',
          }}
        >
          Cinematic Transition
        </p>
      </div>
    </AbsoluteFill>
  );
};

// ============================================================================
// #304 Perlin Noise Flow Field (Perlin Noise 流场效果)
// ============================================================================
// 功能：
// - Perlin Noise 噪声算法生成自然流动
// - 粒子跟随流场移动
// - 渐变颜色映射
// - 无限循环动画
// 
// 技术栈：
// - Simplex Noise 算法（简化版）
// - 粒子物理系统
// - SVG 渲染
// - 发光滤镜
// 
// 适用：背景装饰、生成艺术、自然主题
// ============================================================================

export const PerlinNoiseFlowField: React.FC = () => {
  const frame = useCurrentFrame();
  const { width, height, fps } = useVideoConfig();
  
  const time = frame / fps;
  
  // 简化版 Perlin Noise 函数（2D）
  const noise2D = (x: number, y: number, seed: number = 0): number => {
    // 使用 sin 函数模拟噪声（简化版）
    return (
      Math.sin(x * 0.01 + seed) * Math.cos(y * 0.01 + seed) +
      Math.sin(x * 0.02 - y * 0.02 + seed * 1.5) * 0.5 +
      Math.cos(x * 0.03 + y * 0.03 - seed * 0.7) * 0.25
    ) / 1.75;
  };
  
  // 粒子配置
  const particleCount = 200;
  
  // 生成粒子数据（基于流场）
  const particles = Array.from({ length: particleCount }, (_, i) => {
    // 初始位置（随机分布）
    const baseX = (i % 20) * (width / 19);
    const baseY = Math.floor(i / 20) * (height / 9);
    
    // 计算流场角度
    const noiseValue = noise2D(baseX, baseY, time * 0.5);
    const angle = noiseValue * Math.PI * 4; // -2π to 2π
    
    // 根据流场移动粒子
    const speed = 50;
    const x = baseX + Math.cos(angle) * speed * Math.sin(time + i * 0.1);
    const y = baseY + Math.sin(angle) * speed * Math.cos(time + i * 0.1);
    
    // 颜色基于流场值和位置
    const hue = interpolate(noiseValue, [-1, 1], [180, 280]); // 青色到洋红色
    const saturation = 0.7;
    const lightness = interpolate(noiseValue, [-1, 1], [0.4, 0.7]);
    
    // 粒子大小基于流场强度
    const size = interpolate(Math.abs(noiseValue), [0, 1], [2, 6], {
      extrapolateRight: 'clamp',
    });
    
    // 透明度脉动
    const opacity = interpolate(
      Math.sin(time * 2 + i * 0.2),
      [-1, 1],
      [0.4, 0.9]
    );
    
    return {
      x,
      y,
      size,
      hue,
      saturation,
      lightness,
      opacity,
      noiseValue,
    };
  });
  
  // 背景网格（流场可视化）
  const gridSize = 40;
  const gridLines = [];
  
  for (let x = 0; x < width; x += gridSize) {
    for (let y = 0; y < height; y += gridSize) {
      const noiseValue = noise2D(x, y, time * 0.3);
      const angle = noiseValue * Math.PI * 2;
      const length = Math.abs(noiseValue) * gridSize * 0.8;
      
      const opacity = interpolate(Math.abs(noiseValue), [0, 1], [0.05, 0.2]);
      
      gridLines.push(
        <line
          key={`${x}-${y}`}
          x1={x}
          y1={y}
          x2={x + Math.cos(angle) * length}
          y2={y + Math.sin(angle) * length}
          stroke={`hsla(${interpolate(noiseValue, [-1, 1], [180, 280])}, 60%, 50%, ${opacity})`}
          strokeWidth={1}
        />
      );
    }
  }
  
  // 粒子轨迹（尾部效果）
  const trails = particles.slice(0, 50).map((particle, i) => {
    const trailLength = 5;
    const trailPoints = Array.from({ length: trailLength }, (_, j) => {
      const delay = j * 0.1;
      const pastNoise = noise2D(particle.x, particle.y, (time - delay) * 0.5);
      const pastAngle = pastNoise * Math.PI * 4;
      const pastX = particle.x - Math.cos(pastAngle) * 20 * j;
      const pastY = particle.y - Math.sin(pastAngle) * 20 * j;
      return `${j === 0 ? 'M' : 'L'}${pastX},${pastY}`;
    }).join(' ');
    
    return (
      <path
        key={i}
        d={trailPoints}
        fill="none"
        stroke={`hsla(${particle.hue}, ${particle.saturation * 100}%, ${particle.lightness * 100}%, ${particle.opacity * 0.3})`}
        strokeWidth={particle.size * 0.5}
        strokeLinecap="round"
      />
    );
  });
  
  // 中心文字动画
  const textProgress = spring({
    frame: frame - 30,
    fps,
    config: {
      damping: 15,
      stiffness: 100,
    },
  });
  
  const textScale = textProgress;
  const textOpacity = textProgress;
  
  // 装饰圆环
  const rings = Array.from({ length: 3 }, (_, i) => {
    const scale = spring({
      frame: frame - i * 10,
      fps,
      config: {
        damping: 12,
        stiffness: 80,
      },
    });
    
    const rotation = time * 0.2 * (i % 2 === 0 ? 1 : -1);
    
    return (
      <div
        key={i}
        style={{
          position: 'absolute',
          width: 600 + i * 100,
          height: 600 + i * 100,
          borderRadius: '50%',
          border: `2px solid hsla(${200 + i * 40}, 70%, 60%, ${0.2 - i * 0.05})`,
          transform: `rotate(${rotation}rad) scale(${scale})`,
          opacity: scale,
        }}
      />
    );
  });
  
  return (
    <AbsoluteFill
      style={{
        backgroundColor: '#050510',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        overflow: 'hidden',
      }}
    >
      {/* 背景网格（流场可视化） */}
      <svg
        width={width}
        height={height}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          opacity: 0.5,
        }}
      >
        {gridLines}
      </svg>
      
      {/* 粒子轨迹层 */}
      <svg
        width={width}
        height={height}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
        }}
      >
        <defs>
          <filter id="glow304" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="2" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        
        {/* 轨迹 */}
        {trails}
      </svg>
      
      {/* 主粒子层 */}
      <svg
        width={width}
        height={height}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
        }}
      >
        {particles.map((particle, i) => {
          const color = `hsla(${particle.hue}, ${particle.saturation * 100}%, ${particle.lightness * 100}%, ${particle.opacity})`;
          
          return (
            <circle
              key={i}
              cx={particle.x}
              cy={particle.y}
              r={particle.size}
              fill={color}
              filter="url(#glow304)"
            />
          );
        })}
      </svg>
      
      {/* 装饰圆环 */}
      {rings}
      
      {/* 中心文字 */}
      <div
        style={{
          position: 'absolute',
          transform: `scale(${textScale})`,
          opacity: textOpacity,
          textAlign: 'center',
          zIndex: 10,
        }}
      >
        <h1
          style={{
            fontSize: 80,
            fontWeight: 'bold',
            background: 'linear-gradient(135deg, #00d4ff 0%, #aa00ff 50%, #ff00aa 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            color: 'transparent',
            margin: 0,
            textShadow: '0 0 40px rgba(100, 150, 255, 0.5)',
            letterSpacing: 3,
          }}
        >
          Flow Field
        </h1>
        <p
          style={{
            fontSize: 26,
            color: '#999',
            marginTop: 20,
            letterSpacing: 4,
          }}
        >
          Perlin Noise Animation
        </p>
      </div>
    </AbsoluteFill>
  );
};
